import React, { useState, useEffect } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import "./pools.scss";
import Navbar from "../landing/header/Navbar";
import Table from "react-bootstrap/Table";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useTheme } from "../../hooks/ThemeProvider";
import { apiRequest } from "../../api/apiService";
import { ApiUrl } from "../../utils/ApiUrl";
import Tvl from "../../shared/Tvl";
import PaginationSection from "../../shared/PaginationSection";
import { formatAddress } from "../../helper";
import { unifarmFactory } from "../../utils/contract";
import { STAKES } from "../../api/queries";
import Loader from "../../shared/Loader";
import { defaultUserProfile } from "../../utils/constant";
import ChildPoolApr from "../../shared/ChildPoolApr";

const Pools = () => {
  const { theme } = useTheme();
  const history = useHistory()
  const [rootPools, setRootPools] = useState([]);
  const [childPools, setChildPools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [childPoolCount, setChildPoolCount] = useState(0);
  const [rootPoolsCount, setRootPoolsCount] = useState(0);

  // ✅ Pagination States
  const [rootCurrentPage, setRootCurrentPage] = useState(1);
  const [rootPerPage, setRootPerPage] = useState(10);

  const [childCurrentPage, setChildCurrentPage] = useState(1);
  const [childPerPage, setChildPerPage] = useState(10);

  // ✅ Separate search and sort states for Root Pools
  const [rootSearchTerm, setRootSearchTerm] = useState("");
  const [rootSortOption, setRootSortOption] = useState("projectName@ASC");
  const [rootDebouncedSearch, setRootDebouncedSearch] = useState("");

  // ✅ Separate search and sort states for Child Pools
  const [childSearchTerm, setChildSearchTerm] = useState("");
  const [childSortOption, setChildSortOption] = useState("creatorFeePercent@DESC");
  const [childDebouncedSearch, setChildDebouncedSearch] = useState("");
  const [activeTab, setActiveTab] = useState("rootPools");
  const [childTvlMap, setChildTvlMap] = useState({});


  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const id = query.get("creatorAddress");

  // ✅ Debounce for Root Pool search
  useEffect(() => {
    const handler = setTimeout(() => {
      setRootDebouncedSearch(rootSearchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [rootSearchTerm]);

  // ✅ Debounce for Child Pool search
  useEffect(() => {
    const handler = setTimeout(() => {
      setChildDebouncedSearch(childSearchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [childSearchTerm]);

  // ✅ Fetch root pools
  const fetchRootPools = async (
    page = rootCurrentPage,
    limit = rootPerPage,
    search = rootDebouncedSearch,
    sort = rootSortOption
  ) => {
    try {
      setLoading(true);
      const offset = (page - 1) * limit;

      const apiUrl = `${ApiUrl}/pool/root-pools?limit=${limit}&offset=${offset}&filter=walletAddress=${"eq:" + id
        }${search ? `&search=${encodeURIComponent(search)}` : ""}&sort=${sort}`;

      const response = await apiRequest({
        url: apiUrl,
        method: "GET",
        auth: false,
      });

      setRootPoolsCount(response?.data?.pagination?.total || 0);
      setRootPools(response?.data?.items || []);
    } catch (err) {
      console.error("Error fetching root pools:", err);
      setError(err.message || "Something went wrong fetching root pools");
    } finally {
      setLoading(false);
    }
  };

  const handleChildTvlChange = (poolId, tvlValue) => {
    setChildTvlMap(prev => ({
      ...prev,
      [poolId]: tvlValue
    }));
  };




  // ✅ Fetch child pools (with staker counts)
  const fetchChildPools = async (
    page = childCurrentPage,
    limit = childPerPage,
    search = childDebouncedSearch,
    sort = childSortOption
  ) => {
    try {
      setLoading(true);
      const offset = (page - 1) * limit;

      const apiUrl = `${ApiUrl}/pool/users-child-pools?limit=${limit}&offset=${offset}&filter=walletAddress=${"eq:" + id
        }${search ? `&search=${encodeURIComponent(search)}` : ""}&sort=${sort}`;

      const response = await apiRequest({
        url: apiUrl,
        method: "GET",
        auth: false,
      });

      let pools = response?.data?.items || [];
      setChildPoolCount(response?.data?.pagination?.total || 0);

      const poolsWithStakers = await Promise.all(
        pools.map(async (pool) => {
          try {
            const res = await apiRequest({
              url: unifarmFactory.rootTokens,
              method: "POST",
              data: {
                query: STAKES,
                variables: { where: { contract: pool.contractAddress } },
              },
              auth: false,
            });

            const stakeDatas = res?.data?.stakes || [];
            return { ...pool, stakersCount: stakeDatas.length };
          } catch (error) {
            console.error(`Failed to fetch stakers for ${pool.contractAddress}`, error);
            return { ...pool, stakersCount: 0 };
          }
        })
      );

      setChildPools(poolsWithStakers);
    } catch (err) {
      console.error("Error fetching child pools:", err);
      setError(err.message || "Something went wrong fetching child pools");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Fetch when ID or pagination changes
  useEffect(() => {
    if (id) fetchRootPools(rootCurrentPage, rootPerPage, rootDebouncedSearch, rootSortOption);
  }, [id, rootCurrentPage, rootPerPage, rootDebouncedSearch, rootSortOption]);

  useEffect(() => {
    if (id) fetchChildPools(childCurrentPage, childPerPage, childDebouncedSearch, childSortOption);
  }, [id, childCurrentPage, childPerPage, childDebouncedSearch, childSortOption]);

  // Helper to get sort label
  const getSortLabel = (sortOption) => {
    switch (sortOption) {
      case "projectName@ASC":
        return "Name Ascending";
      case "projectName@DESC":
        return "Name Descending";
      case "apr@DESC":
        return "APR";
      case "creatorFeePercent@DESC":
        return "Creator Fee";
      default:
        return "TVL";
    }
  };

  if (loading) {
    return <Loader loadingText={"Loading Pools..."} />
  }
  return (
    <>
      <section className="rootpools_main">
        {
          theme === 'dark' ? <img src="\assets\new.png" className="detailshade" alt="shade" />
            : <img src='\assets\whiteshade.png' className='detailshade' />
        }

        <div className="custom-container">
          <div className="inner_future">
            <div className="top_future">
              <div className="inner_top">
                <img src={childPools[0]?.createdBy?.imageUrl ?? defaultUserProfile?.imageUrl} className="imggdra" alt="logo" />
                <div>
                  <h4>{childPools.length > 0 && childPools?.[0]?.createdBy?.name || defaultUserProfile.name}</h4>
                  <p>{formatAddress(id)}</p>
                </div>
              </div>
              <div className="rightttside">
                {/* Safely access social links */}
                {childPools?.[0]?.createdBy?.socialLinks && (
                  <>
                    {/* Twitter */}
                    {childPools[0].createdBy?.socialLinks?.twitter && (
                      <a
                        href={childPools[0].createdBy.socialLinks.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {theme === "dark" ? (
                          <img src="\assets\x.svg" alt="Twitter" />
                        ) : (
                          <img src="\assets\lightx.svg" alt="Twitter" />
                        )}
                      </a>
                    )}

                    {/* Instagram */}
                    {childPools[0].createdBy?.socialLinks?.instagram && (
                      <a
                        href={childPools[0].createdBy.socialLinks.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {theme === "dark" ? (
                          <img src="\assets\insta.svg" alt="Instagram" />
                        ) : (
                          <img src="\assets\lightin.svg" alt="Instagram" />
                        )}
                      </a>
                    )}

                    {/* Discord */}
                    {childPools[0].createdBy?.socialLinks?.discord && (
                      <a
                        href={childPools[0].createdBy.socialLinks.discord}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {theme === "dark" ? (
                          <img src="\assets\dis.svg" alt="Discord" />
                        ) : (
                          <img src="\assets\lightdis.svg" alt="Discord" />
                        )}
                      </a>
                    )}
                  </>
                )}
              </div>

            </div>

            <p>
              {childPools.length > 0 && childPools?.[0]?.createdBy?.description || defaultUserProfile?.description}
            </p>

            <div className="chart_side">
              <div className="inner_future">
                <p>Root Pool Created</p>
                <h4>{rootPoolsCount}</h4>
              </div>
              <div className="inner_future">
                <p>Child Pool Created</p>
                <h4>{childPoolCount}</h4>
              </div>
            </div>

            {/* TABS */}
            <div className="innner_topp">
              <div>
                <div className="uppper_tabs ">
                  <ul className="nav nav-pills " id="pills-tab" role="tablist">
                    <li className="nav-item" role="presentation">
                      <button
                        className="nav-link active"
                        id="pills-home-tab"
                        data-bs-toggle="pill"
                        data-bs-target="#pills-home"
                        type="button"
                        role="tab"
                        aria-controls="pills-home"
                        aria-selected={activeTab === "rootPools" ? "true" : "false"}
                        onClick={() => setActiveTab("rootPools")}
                      >
                        Root Pools
                      </button>
                    </li>
                    <li className="nav-item" role="presentation">
                      <button
                        className="nav-link"
                        id="pills-profile-tab"
                        data-bs-toggle="pill"
                        data-bs-target="#pills-profile"
                        type="button"
                        role="tab"
                        aria-controls="pills-profile"
                        aria-selected={activeTab === "childPools" ? "true" : "false"}
                        onClick={() => setActiveTab("childPools")}
                      >
                        Child Pools
                      </button>
                    </li>
                  </ul>

                </div>

                {/* TAB CONTENT */}
                <div className="tab-content" id="pills-tabContent">
                  {/* ROOT POOLS TAB */}
                  <div
                    className="tab-pane fade show active"
                    id="pills-home"
                    role="tabpanel"
                    aria-labelledby="pills-home-tab"
                  >
                    {/* ROOT POOL SEARCH & SORT */}

                    <div className="search_Side leftsearchhh">
                      <div className="inner_Search">
                        <img src="\assets\searchicon.svg" className="searchicon" alt="search" />
                        <input
                          placeholder="Search by name or address"
                          type="text"
                          value={rootSearchTerm}
                          onChange={(e) => {
                            setRootSearchTerm(e.target.value);
                            setRootCurrentPage(1);
                          }}
                        />
                      </div>
                      <Dropdown>
                        <Dropdown.Toggle variant="success" id="dropdown-basic">
                          Sort by <span>{getSortLabel(rootSortOption)}</span>
                          <div>
                            {theme === 'dark' ? (
                              <img src="\assets\droparrow.svg" alt="drop" />
                            ) : (
                              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <path d="M3 4.5L6 7.5L9 4.5" stroke="#000F2F" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </div>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={() => setRootSortOption("projectName@ASC")}>
                            <div className="drop_inside">
                              <h5>Sort by: <span>Name Ascending</span></h5>
                              {rootSortOption === "projectName@ASC" && <img src="\assets\bluetick.svg" alt="tick" />}
                            </div>
                          </Dropdown.Item>

                          <Dropdown.Item onClick={() => setRootSortOption("projectName@DESC")}>
                            <div className="drop_inside">
                              <h5>Sort by: <span>Name Descending</span></h5>
                              {rootSortOption === "projectName@DESC" && <img src="\assets\bluetick.svg" alt="tick" />}
                            </div>
                          </Dropdown.Item>

                          {/* <Dropdown.Item onClick={() => setRootSortOption("apr@DESC")}>
                            <div className="drop_inside">
                              <h5>Sort by: <span>APR</span></h5>
                              {rootSortOption === "apr@DESC" && <img src="\assets\bluetick.svg" alt="tick" />}
                            </div>
                          </Dropdown.Item>

                          <Dropdown.Item onClick={() => setRootSortOption("tvl@DESC")}>
                            <div className="drop_inside">
                              <h5>Sort by: <span>TVL</span></h5>
                              {rootSortOption === "tvl@DESC" && <img src="\assets\bluetick.svg" alt="tick" />}
                            </div>
                          </Dropdown.Item> */}
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                    {/* DESKTOP TABLE */}
                    <div className="table_main">
                      <div className="table_innner">
                        <p className="one_sidee">#</p>
                        <p className="poolname">Pool Name</p>
                        <p>TVL</p>
                        <p>Rewards Funded</p>
                        {/* <p>Remaining Rewards</p> */}
                        <p>End Date/Time</p>
                        <p>Active Child Pools</p>
                        <p>Status</p>
                        <p></p>
                      </div>

                      {rootPools.length > 0 ? (
                        rootPools.map((pool, index) => {
                          const endDate = new Date(pool.poolEndDate);
                          const isExpired = endDate < new Date();

                          return (
                            <div className="table_innner" key={pool._id}>
                              <h4 className="one_sidee">
                                {(rootCurrentPage - 1) * rootPerPage + (index + 1)}
                              </h4>
                              <div className="under_table LASTTTABLE">
                                <img
                                  src={pool.projectLogo || "/assets/default.svg"}
                                  className="imgtable"
                                  alt="project logo"
                                />
                                <div>
                                  <h6>{pool.projectName || "Unnamed Pool"}</h6>
                                  <h5>{pool.tagline || "-"}</h5>
                                </div>
                              </div>

                              <h4>
                                <Tvl
                                  contractAddress={pool.contractAddress}
                                  poolType={"rootPool"}
                                />
                                {" " + (pool?.symbol || "")}
                              </h4>
                              <h4>{pool.rewardAmount?.toLocaleString() + " " + (pool?.symbol || "")}</h4>
                              {/* <h4>$0</h4> */}

                              <div className="under_table">
                                <div>
                                  <h6>
                                    {endDate.toLocaleDateString("en-US", {
                                      month: "short",
                                      day: "2-digit",
                                      year: "numeric",
                                    })}
                                  </h6>
                                  <h5>
                                    {endDate.toLocaleTimeString("en-US", {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })}
                                  </h5>
                                </div>
                              </div>

                              <h4>{pool.childPoolCount || 0}</h4>
                              <div className={isExpired ? "expire" : "active"}>
                                {isExpired ? "Expired" : "Active"}
                              </div>
                              <div className="butttons_div">
                                <button onClick={() => history.push(`/activepools/${pool._id}`)}>
                                  Details{" "}
                                  {theme === "dark" ? (
                                    <img src="/assets/white.svg" alt="arrow" />
                                  ) : (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="12"
                                      height="12"
                                      viewBox="0 0 12 12"
                                      fill="none"
                                    >
                                      <path
                                        d="M9 4L11 6L9 8"
                                        stroke="#000F2F"
                                        strokeWidth="1.25"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                      <path
                                        d="M1 6H11"
                                        stroke="#000F2F"
                                        strokeWidth="1.25"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  )}
                                </button>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <span className="midddddke" style={{ textAlign: "center", marginTop: "20px" }}>
                          {loading ? "Loading..." : "No pools found"}
                        </span>
                      )}
                    </div>

                    {/* MOBILE UI FOR ROOT POOLS */}
                    <div className="formobile">
                      <div className="mobilebox d-none">
                        {rootPools.length > 0 ? (
                          rootPools.map((pool, index) => {
                            const endDate = new Date(pool.poolEndDate);
                            const isExpired = endDate < new Date();

                            return (
                              <div className="innerboxmain" key={pool._id}>
                                <div className="parentinner">
                                  <div className="firstinnner">
                                    <h6>#</h6>
                                    <h5>{(rootCurrentPage - 1) * rootPerPage + (index + 1)}</h5>
                                  </div>
                                  <div className="secondinnner">
                                    <h6>Pool Name</h6>
                                    <div className="parenttext">
                                      <span>
                                        <img
                                          src={pool.projectLogo || "/assets/default.svg"}
                                          className="imgtable"
                                          alt="pool"
                                        />
                                      </span>
                                      <div className="mainnnn">
                                        <h5>{pool.projectName || "Unnamed Pool"}</h5>
                                        <h6>{pool.tagline || "-"}</h6>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="secondinnner">
                                    <h6>Status</h6>
                                    <h5 className={isExpired ? "yellow" : "green"}>
                                      {isExpired ? "Expired" : "Active"}
                                    </h5>
                                  </div>
                                  <div className="thirdinnner">
                                    <div className="butttons_div">
                                      <button onClick={() => history.push(`/activepools/${pool._id}`)}>
                                        Details{" "}
                                        {theme === 'dark' ? (
                                          <img src="/assets/white.svg" alt="arrow" />
                                        ) : (
                                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                            <path d="M9 4L11 6L9 8" stroke="#000F2F" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M1 6H11" stroke="#000F2F" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                                          </svg>
                                        )}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                                <div className="mainbottom">
                                  <div className="innerbottomtex">
                                    <h6 className="live">TVL</h6>
                                    <h5 className="num">
                                      <Tvl
                                        contractAddress={pool.contractAddress}
                                        poolType={"rootPool"}
                                      />
                                      {(pool?.symbol || "")}
                                    </h5>
                                  </div>
                                  <div className="innerbottomtex">
                                    <h6 className="live">Rewards Funded</h6>
                                    <h5 className="num">${pool.rewardAmount?.toLocaleString() || "0"}</h5>
                                  </div>
                                  {/* <div className="innerbottomtex">
                                    <h6 className="live">Remaining Rewards</h6>
                                    <h5 className="num">$0</h5>
                                  </div> */}
                                  <div className="innerbottomtex">
                                    <h6 className="live">End Date/Time</h6>
                                    <h5 className="num">
                                      {endDate.toLocaleDateString("en-US", {
                                        month: "short",
                                        day: "2-digit",
                                        year: "numeric",
                                      })}{" "}
                                      <span className="numspan">
                                        {endDate.toLocaleTimeString("en-US", {
                                          hour: "2-digit",
                                          minute: "2-digit",
                                        })}
                                      </span>
                                    </h5>
                                  </div>
                                  <div className="innerbottomtex">
                                    <h6 className="live">Active Child Pools</h6>
                                    <h5 className="num">{pool.childPoolCount || 0}</h5>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <p style={{ textAlign: "center", marginTop: "20px" }}>
                            {loading ? "Loading..." : "No root pools found"}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* ✅ PAGINATION ROOT */}
                    <PaginationSection
                      currentPage={rootCurrentPage}
                      totalItems={rootPoolsCount}
                      perPage={rootPerPage}
                      onPageChange={setRootCurrentPage}
                      onPerPageChange={(val) => {
                        setRootPerPage(val);
                        setRootCurrentPage(1);
                      }}
                    />
                  </div>

                  {/* CHILD POOLS TAB */}
                  <div
                    className="tab-pane fade"
                    id="pills-profile"
                    role="tabpanel"
                    aria-labelledby="pills-profile-tab"
                  >
                    {/* CHILD POOL SEARCH & SORT */}
                    <div className="search_Side leftsearchhh">
                      <div className="inner_Search">
                        <img src="\assets\searchicon.svg" className="searchicon" alt="search" />
                        <input
                          placeholder="Search by wallet address"
                          type="text"
                          value={childSearchTerm}
                          onChange={(e) => {
                            setChildSearchTerm(e.target.value);
                            setChildCurrentPage(1);
                          }}
                        />
                      </div>
                      <Dropdown>
                        <Dropdown.Toggle variant="success" id="dropdown-basic-child">
                          Sort by <span>{getSortLabel(childSortOption)}</span>
                          <div>
                            {theme === 'dark' ? (
                              <img src="\assets\droparrow.svg" alt="drop" />
                            ) : (
                              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <path d="M3 4.5L6 7.5L9 4.5" stroke="#000F2F" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </div>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          {/* <Dropdown.Item onClick={() => setChildSortOption("projectName@ASC")}>
                            <div className="drop_inside">
                              <h5>Sort by: <span>Name Ascending</span></h5>
                              {childSortOption === "projectName@ASC" && <img src="\assets\bluetick.svg" alt="tick" />}
                            </div>
                          </Dropdown.Item>

                          <Dropdown.Item onClick={() => setChildSortOption("projectName@DESC")}>
                            <div className="drop_inside">
                              <h5>Sort by: <span>Name Descending</span></h5>
                              {childSortOption === "projectName@DESC" && <img src="\assets\bluetick.svg" alt="tick" />}
                            </div>
                          </Dropdown.Item>

                          <Dropdown.Item onClick={() => setChildSortOption("apr@DESC")}>
                            <div className="drop_inside">
                              <h5>Sort by: <span>APR</span></h5>
                              {childSortOption === "apr@DESC" && <img src="\assets\bluetick.svg" alt="tick" />}
                            </div>
                          </Dropdown.Item>

                          <Dropdown.Item onClick={() => setChildSortOption("tvl@DESC")}>
                            <div className="drop_inside">
                              <h5>Sort by: <span>TVL</span></h5>
                              {childSortOption === "tvl@DESC" && <img src="\assets\bluetick.svg" alt="tick" />}
                            </div>
                          </Dropdown.Item> */}

                          <Dropdown.Item onClick={() => setChildSortOption("creatorFeePercent@DESC")}>
                            <div className="drop_inside">
                              <h5>Sort by: <span>Creator Fee</span></h5>
                              {childSortOption === "creatorFeePercent@DESC" && <img src="\assets\bluetick.svg" alt="tick" />}
                            </div>
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>

                    {/* DESKTOP TABLE */}
                    <div className="table_main">
                      <div className="table_innner">
                        <p className="one_sidee">#</p>
                        <p className="poolname">Root Pool</p>
                        <p>Live APR</p>
                        <p>TVL</p>
                        {/* <p>Claimable/Total Rewards</p> */}
                        <p>Creator Fee%</p>
                        <p>Stakers count</p>
                        <p>Status</p>
                        <p></p>
                      </div>

                      {childPools.length > 0 ? (

                        childPools.map((pool, index) => {
                          const endDate = new Date(pool.rootPoolId?.poolEndDate);
                          const isExpired = endDate < new Date();

                          // Get TVL from stored map
                          const tvl = childTvlMap[pool._id] || 0;

                          // Calculate APR dynamically


                          // calculateChildPoolAPR(
                          //   pool.rootPoolId?.rewardAmount,
                          //   pool.rootPoolId?.poolEndDate,
                          //   tvl,
                          //   pool.creatorFeePercent
                          // );

                          return (
                            <div className="table_innner" key={pool._id}>
                              <h4 className="one_sidee">{(childCurrentPage - 1) * childPerPage + (index + 1)}</h4>

                              <div className="under_table LASTTTABLE">
                                <img src={pool?.rootPoolId?.projectLogo || "/assets/default.svg"} className="imgtable" alt="pool" />
                                <div>
                                  <h6>{pool?.rootPoolId?.projectName || "Unnamed Pool"}</h6>
                                  <h5>{pool.tagline || "-"}</h5>
                                </div>
                              </div>

                              {/* ✅ LIVE APR based on TVL */}
                              {pool?.contractAddress &&
                                pool?.rootPoolId?.contractAddress &&
                                pool?.rootPoolId?.poolEndDate &&
                                pool?.rootPoolId?.rewardAmount ? (
                                <ChildPoolApr
                                  childPoolContractAddress={pool.contractAddress}
                                  rootPoolAddress={pool.rootPoolId.contractAddress}
                                  rootPoolEndDate={pool.rootPoolId.poolEndDate}
                                  rootPoolRewardAmount={pool.rootPoolId.rewardAmount}
                                  rootPoolStartDate={pool.rootPoolId.createdAt}
                                />
                              ) : (
                                0
                              )}
                              {/* ✅ Pass callback to capture TVL value */}
                              <h4>
                                <Tvl
                                  contractAddress={pool.contractAddress}
                                  poolType="childPool"
                                  setTvlAmount={(val) => handleChildTvlChange(pool._id, val)}
                                />
                                {" " + (pool?.rootPoolId?.symbol || "")}
                              </h4>

                              <h4>{pool.creatorFeePercent}%</h4>
                              <h4>{pool.stakersCount || 0}</h4>

                              <div className={isExpired ? "expire" : "active"}>
                                {isExpired ? "Expired" : "Active"}
                              </div>

                              <div className="butttons_div">
                                <button onClick={() => history.push(`/stakingpool?id=${pool._id}`)}>
                                  Details
                                  {theme === "dark" ? (
                                    <img src="/assets/white.svg" alt="arrow" />
                                  ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                      <path d="M9 4L11 6L9 8" stroke="#000F2F" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                                      <path d="M1 6H11" stroke="#000F2F" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                  )}
                                </button>
                              </div>
                            </div>
                          );
                        })


                      ) : (
                        <span className="midddddke" style={{ textAlign: "center", marginTop: "20px" }}>
                          {loading ? "Loading..." : "No child pools found"}
                        </span>
                      )}
                    </div>

                    {/* MOBILE UI FOR CHILD POOLS */}
                    <div className="formobile">
                      <div className="mobilebox d-none">
                        {childPools.length > 0 ? (
                          childPools.map((pool, index) => {
                            const endDate = new Date(pool.rootPoolId?.poolEndDate);
                            const isExpired = endDate < new Date();

                            // TVL value from map
                            const tvl = childTvlMap[pool._id] || 0;

                            // Calculate APR dynamically


                            return (
                              <div className="table_inner_two" key={pool._id}>
                                <div className="inner_table">
                                  <div className="top_heading">
                                    <h6>{pool?.rootPoolId?.projectName || "Unnamed Pool"}</h6>
                                    <div className={isExpired ? "expire" : "active"}>
                                      {isExpired ? "Expired" : "Active"}
                                    </div>
                                  </div>

                                  <div className="main_content">
                                    <div className="img_area">
                                      <img src={pool?.rootPoolId?.projectLogo || "/assets/default.svg"} alt="pool" />
                                    </div>

                                    <div className="details_area">
                                      <div className="detail_row">
                                        <p>APR</p>
                                        <h5>
                                          {pool?.contractAddress &&
                                            pool?.rootPoolId?.contractAddress &&
                                            pool?.rootPoolId?.poolEndDate &&
                                            pool?.rootPoolId?.rewardAmount ? (
                                            <ChildPoolApr
                                              childPoolContractAddress={pool.contractAddress}
                                              rootPoolAddress={pool.rootPoolId.contractAddress}
                                              rootPoolEndDate={pool.rootPoolId.poolEndDate}
                                              rootPoolRewardAmount={pool.rootPoolId.rewardAmount}
                                              rootPoolStartDate={pool.rootPoolId.createdAt}
                                            />
                                          ) : (
                                            0
                                          )}
                                        </h5>

                                      </div>

                                      <div className="detail_row">
                                        <p>TVL</p>
                                        <h5>
                                          <Tvl
                                            contractAddress={pool.contractAddress}
                                            poolType="childPool"
                                            setTvlAmount={(val) => handleChildTvlChange(pool._id, val)}
                                          />
                                          {" " + (pool?.rootPoolId?.symbol || "")}
                                        </h5>
                                      </div>

                                      <div className="detail_row">
                                        <p>Creator Fee</p>
                                        <h5>{pool.creatorFeePercent}%</h5>
                                      </div>

                                      <div className="detail_row">
                                        <p>Stakers</p>
                                        <h5>{pool.stakersCount || 0}</h5>
                                      </div>
                                    </div>
                                  </div>

                                  <div className="bottom_btn">
                                    <button onClick={() => history.push(`/stakingpool?id=${pool._id}`)}>
                                      Details
                                      {theme === "dark" ? (
                                        <img src="/assets/white.svg" alt="arrow" />
                                      ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                          <path d="M9 4L11 6L9 8" stroke="#000F2F" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                                          <path d="M1 6H11" stroke="#000F2F" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                      )}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })

                        ) : (
                          <p style={{ textAlign: "center", marginTop: "20px" }}>
                            {loading ? "Loading..." : "No child pools found"}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* ✅ PAGINATION CHILD */}
                    <PaginationSection
                      currentPage={childCurrentPage}
                      totalItems={childPoolCount}
                      perPage={childPerPage}
                      onPageChange={setChildCurrentPage}
                      onPerPageChange={(val) => {
                        setChildPerPage(val);
                        setChildCurrentPage(1);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Pools;